import { useLayoutEffect } from 'react';
import { NavigationType, useLocation, useNavigationType } from 'react-router-dom';

/**
 * Opens every newly visited page at its top.
 *
 * A browser does this by itself on a real page load, and a single-page app has to do it by
 * hand: the document never changes, so neither does its scroll offset. Without this, the
 * home page's closing "Learn more" button -- which sits at the very bottom -- opened About
 * scrolled most of the way down, and every other in-app link did the same to a lesser degree.
 * React Router's own `<ScrollRestoration>` would do it, but only under a data router, and the
 * app uses `BrowserRouter`.
 *
 * Three decisions, each deliberate:
 *
 * - **Only a new page, not a new query string.** It keys on the pathname, so a page that
 *   writes its filters into the URL does not throw the reader back to the top on a keystroke.
 * - **Back and forward are left alone** (`POP`). Returning to a page is the one case where the
 *   previous offset is the right one, and the browser restores it where it can.
 * - **`instant`, never the default.** The theme gives `<html>` `scroll-behavior: smooth`, which
 *   a plain `scrollTo(0, 0)` obeys -- so the new page would visibly glide up from wherever the
 *   old one was left. A layout effect, so the jump lands before the first paint rather than
 *   after it.
 *
 * Renders nothing. Mounted once, inside the router, in `App`.
 */
export const ScrollToTop = (): null => {
    const { pathname, hash } = useLocation();
    const navigationType: NavigationType = useNavigationType();

    useLayoutEffect(() => {
        // An anchor link means "take me to that part of the page", which the top is not.
        if (navigationType === NavigationType.Pop || hash.length > 0) {
            return;
        }

        // 'instant' is in the spec and every browser, but not in TypeScript 4.9's DOM types.
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
        // The navigation type is read, not a trigger: only a change of page should scroll.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname]);

    return null;
};
